import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Grid,
  LinearProgress,
} from '@mui/material';
import {
  Settings,
  Refresh,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { DelayAnalysisData } from '../../types/widget';
import { widgetTheme } from '../../theme/darkTheme';
import { apiService } from '../../services/api';

interface DelayAnalysisWidgetProps {
  widgetId: string;
  title: string;
  data?: DelayAnalysisData;
  onRefresh?: () => void;
  onConfigure?: () => void;
  isVisible?: boolean;
  onToggleVisibility?: () => void;
}

// Helper to parse date strings without timezone issues
const parseDateString = (dateStr: string): Date => {
  if (!dateStr) return new Date();
  // Append T12:00:00 to avoid timezone boundary issues
  if (!dateStr.includes('T')) {
    return new Date(dateStr + 'T12:00:00');
  }
  const date = new Date(dateStr);
  date.setHours(12, 0, 0, 0);
  return date;
};

const DelayAnalysisWidget: React.FC<DelayAnalysisWidgetProps> = ({
  widgetId,
  title,
  data,
  onRefresh,
  onConfigure,
  isVisible = true,
  onToggleVisibility,
}) => {
  const [delayData, setDelayData] = useState<DelayAnalysisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDelayData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getDelayAnalysisData();
      if (response.success && response.data) {
        setDelayData(response.data);
      } else {
        setError(response.error || 'Failed to fetch delay analysis data');
      }
    } catch (err) {
      setError('Error fetching delay analysis data');
      console.error('Error fetching delay analysis data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDelayData();
  }, []);

  const handleRefresh = () => {
    fetchDelayData();
    if (onRefresh) {
      onRefresh();
    }
  };

  const currentData = data || delayData;

  if (!isVisible) {
    return null;
  }

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader title={title} subheader="Loading..." />
        <CardContent sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader title={title} subheader="Error" />
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!currentData) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader title={title} subheader="No data available" />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            No delay analysis data available.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Enhanced timing analysis - with safe number conversion
  const totalDelays = currentData.delayIncidents ? currentData.delayIncidents.length : 0;
  const first2Hours = Number(currentData.delayTiming?.first_2_hours) || 0;
  const restOfDay = Number(currentData.delayTiming?.rest_of_day) || 0;
  const totalHoursLost = Number(currentData.totalHoursLost) || 0;
  const first2HoursPercentage = Number(currentData.delayTiming?.first_2_hours_percentage) || 0;
  const restOfDayPercentage = Number(currentData.delayTiming?.rest_of_day_percentage) || 0;

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: '100%', overflow: 'hidden' }}>
      <CardHeader
        title={title}
        subheader={`${totalDelays} total delays • ${totalHoursLost.toFixed(1)} hours lost`}
        action={
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Refresh Data">
              <IconButton onClick={handleRefresh} size="small">
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Configure Widget">
              <IconButton onClick={onConfigure} size="small">
                <Settings />
              </IconButton>
            </Tooltip>
            <Tooltip title={isVisible ? "Hide Widget" : "Show Widget"}>
              <IconButton onClick={onToggleVisibility} size="small">
                {isVisible ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      
      <CardContent sx={{ flexGrow: 1, p: 0, overflow: 'scroll', minHeight: 0 }}>
        <Grid container spacing={2} sx={{ p: 2 }}>
          {/* Summary Cards */}
          <Grid item xs={12}>
            <Box display="flex" gap={2} mb={2}>
              <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
                <Typography variant="h6" color="primary">
                  {totalDelays}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Delays
                </Typography>
              </Paper>
              <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
                <Typography variant="h6" color="secondary">
                  {totalHoursLost.toFixed(1)}h
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Hours Lost
                </Typography>
              </Paper>
              <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
                <Typography variant="h6" color="warning.main">
                  {totalDelays > 0 ? (totalHoursLost / totalDelays).toFixed(1) : '0.0'}h
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg per Delay
                </Typography>
              </Paper>
            </Box>
          </Grid>

          {/* Top Delay Reasons */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 400, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom>
                Top Causes of Delays
              </Typography>
              <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Reason</TableCell>
                        <TableCell align="right">Hours Lost</TableCell>
                        <TableCell align="right">Count</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(currentData.delayReasons || []).slice(0, 5).map((reason, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" sx={{ 
                              maxWidth: 200, 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {reason.delay_reason}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="error.main">
                              {(Number(reason.total_hours_lost) || 0).toFixed(1)}h
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={reason.occurrence_count} 
                              size="small" 
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </Grid>

          {/* Timing Analysis */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 400, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom>
                Timing Analysis
              </Typography>
              <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Box display="flex" gap={2} mb={2}>
                  <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      {first2Hours}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      First 2 Hours
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {first2HoursPercentage.toFixed(1)}%
                    </Typography>
                  </Paper>
                  <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
                    <Typography variant="h4" color="secondary">
                      {restOfDay}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Rest of Day
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {restOfDayPercentage.toFixed(1)}%
                    </Typography>
                  </Paper>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  {first2HoursPercentage > 50 
                    ? 'Most delays occur early in the day'
                    : 'Delays are spread throughout the day'
                  }
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Recent Incidents */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, height: 400, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom>
                Recent Delay Incidents
              </Typography>
              <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Day</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell align="right">Hours Lost</TableCell>
                        <TableCell>Time Range</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(currentData.delayIncidents || []).slice(0, 10).map((incident, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              Day {incident.shoot_day_number}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {parseDateString(incident.shoot_date).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ 
                              maxWidth: 200, 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {incident.delay_reason}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="error.main">
                              {(Number(incident.hours_lost) || 0).toFixed(1)}h
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {incident.delay_start_time && incident.delay_end_time 
                                ? `${new Date(incident.delay_start_time).toLocaleTimeString()} - ${new Date(incident.delay_end_time).toLocaleTimeString()}`
                                : 'N/A'
                              }
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default DelayAnalysisWidget;